/** Maps the company-portal employees API (PROLINE-2613, CompanyUser) to the
 * table row shape used by the Employees Management list. */

import { formatMobileNumber } from "../../../utils/Helpers";

/** Full international digits + stored country dial (digits only) → API fields,
 * matching the company-registration ContactForm. dial_code is sent with a `+`. */
export const splitPhoneForApi = (fullDigits, countryDialDigits) => {
  const digits = String(fullDigits ?? "").replace(/\D/g, "");
  const dial = String(countryDialDigits ?? "966").replace(/\D/g, "");
  const national = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
  return { dial_code: `+${dial}`, phone_number: national };
};

/** Rebuild the full international digit string the phone field expects from a
 * stored national number + dial code (used to hydrate the Edit form). */
export const buildFullInternationalPhone = (phoneNumber, dialCode) => {
  const national = String(phoneNumber ?? "").replace(/\D/g, "");
  if (!national) return "";
  const dial = String(dialCode ?? "").replace(/\D/g, "");
  return `${dial}${national}`;
};

/** Pagination meta from response headers (axios lowercases header keys). */
export const getCompanyUsersListMeta = (response) => ({
  currentPage: Number(response?.headers?.["current-page"] || 1),
  count: Number(
    response?.headers?.["total-count"] ??
      response?.data?.meta?.total_count ??
      response?.data?.meta?.count ??
      0,
  ),
  pages: Number(response?.headers?.["total-pages"] || 1),
});

/** Stable company_user id for list rows and show/update/delete routes. */
export const resolveCompanyUserId = (user = {}) => {
  const nested =
    user?.company_user && typeof user.company_user === "object"
      ? user.company_user
      : null;
  const source = nested || user;
  const attrs = source?.attributes || source || {};

  const raw =
    attrs.id ??
    source.id ??
    user.id ??
    attrs.company_user_id ??
    source.company_user_id ??
    user.company_user_id ??
    attrs.user_id ??
    source.user_id;

  if (raw == null || raw === "") return "";
  return String(raw);
};

/** Unwrap admin/company show/create/update company_user payloads. */
export const extractCompanyUserPayload = (response) => {
  const root = response?.data;
  if (!root) return null;

  const candidates = [
    root.data?.data,
    root.data,
    root.company_user,
    root,
  ].filter(Boolean);

  for (const item of candidates) {
    const nested =
      item?.company_user && typeof item.company_user === "object"
        ? item.company_user
        : null;
    const source = nested || item;
    if (
      source?.attributes ||
      source?.id != null ||
      source?.company_user_id != null ||
      resolveCompanyUserId(item)
    ) {
      return item;
    }
  }

  return null;
};

export const normalizeCompanyUser = (user = {}) => {
  const nested =
    user?.company_user && typeof user.company_user === "object"
      ? user.company_user
      : null;
  const source = nested || user;
  const attrs = source?.attributes || source || {};
  const country = attrs.country || {};
  const companyRole = attrs.company_role || attrs.role || {};
  const companyRoleIsString = typeof companyRole === "string";

  return {
    id: resolveCompanyUserId(user),
    name: attrs.name ?? "",
    email: attrs.email ?? "",
    phoneNumber: attrs.phone_number ?? "",
    dialCode: country.dial_code ?? attrs.dial_code ?? "",
    // ISO2 (e.g. "eg") used to set the phone field's flag in the edit form.
    countryIso: String(country.short_code ?? attrs.country_iso ?? "").toLowerCase(),
    mobileNumber: formatMobileNumber(
      attrs.phone_number,
      country.dial_code ?? attrs.dial_code,
    ),
    role: companyRoleIsString
      ? companyRole
      : companyRole.name ??
        attrs.company_role_name ??
        attrs.role_name ??
        (typeof attrs.role === "string" ? attrs.role : "") ??
        "",
    roleId: companyRoleIsString
      ? attrs.company_role_id ?? null
      : companyRole.id ?? attrs.company_role_id ?? attrs.role_id ?? null,
    image: attrs.profile_picture_url ?? "",
    profileStatus: attrs.profile_status ?? "",
    isCompanyOwner: Boolean(attrs.is_company_owner),
  };
};

export const normalizeCompanyUsers = (list = []) =>
  (Array.isArray(list) ? list : []).map(normalizeCompanyUser);

/** Merge show-response fields with list/navigation fallback (role is slim on show). */
export const mergeEmployeeRecord = (primary, fallback) => {
  if (!primary) return fallback || null;
  if (!fallback) return primary;
  return {
    ...primary,
    role: primary.role || fallback.role || "",
    roleId: primary.roleId ?? fallback.roleId ?? null,
  };
};

/** Resolve the employee's role to a dropdown option (edit form). */
export const resolveCompanyRoleOption = (employee, roleOptions = []) => {
  if (!employee) return null;

  const roleName = String(employee.role ?? "").trim();
  const roleId =
    employee.roleId != null && employee.roleId !== ""
      ? String(employee.roleId)
      : null;

  if (roleId) {
    const match = roleOptions.find(
      (option) => String(option.id) === roleId,
    );
    if (match) return match;
    if (roleName) return { id: roleId, name: roleName };
  }

  if (roleName) {
    const normalizedName = roleName.toLowerCase();
    const byName = roleOptions.find(
      (option) =>
        String(option.name ?? "")
          .trim()
          .toLowerCase() === normalizedName,
    );
    if (byName) return byName;
  }

  return null;
};

/** Snapshot of editable employee fields when entering edit mode. */
export const createEmployeeEditBaseline = (employee, roleOptions = []) => {
  const phoneCountryDialCode =
    String(employee?.dialCode || "966").replace(/\D/g, "") || "966";
  const phone = buildFullInternationalPhone(
    employee?.phoneNumber,
    employee?.dialCode,
  );
  const { dial_code, phone_number } = splitPhoneForApi(
    phone,
    phoneCountryDialCode,
  );
  const resolvedRole = resolveCompanyRoleOption(employee, roleOptions);

  return {
    name: String(employee?.name ?? "").trim(),
    dial_code,
    phone_number,
    roleId:
      resolvedRole?.id != null && resolvedRole.id !== ""
        ? String(resolvedRole.id)
        : employee?.roleId != null && employee.roleId !== ""
          ? String(employee.roleId)
          : "",
    picturePreview: employee?.image || "",
  };
};

/** True when any editable field differs from the edit baseline. */
export const isEmployeeEditDirty = (
  baseline,
  { name, phone, phoneCountryDialCode, role, pictureFile, picturePreview },
) => {
  if (!baseline) return false;

  const trimmedName = String(name ?? "").trim();
  if (trimmedName !== baseline.name) return true;

  const { dial_code, phone_number } = splitPhoneForApi(
    phone,
    phoneCountryDialCode,
  );
  if (
    dial_code !== baseline.dial_code ||
    phone_number !== baseline.phone_number
  ) {
    return true;
  }

  const roleId = role?.id != null ? String(role.id) : "";
  if (roleId !== baseline.roleId) return true;

  if (pictureFile) return true;
  if ((picturePreview || "") !== baseline.picturePreview) return true;

  return false;
};

/** PATCH body with only fields that changed since edit baseline. */
export const buildUpdateEmployeePayload = (
  baseline,
  {
    name,
    phone,
    phoneCountryDialCode,
    role,
    pictureFile,
    picturePreview,
    profilePictureUrl,
    employeeImage,
  },
) => {
  if (!baseline) return {};

  const data = {};
  const trimmedName = String(name ?? "").trim();
  if (trimmedName !== baseline.name) {
    data.name = trimmedName;
  }

  const roleId = role?.id != null ? String(role.id) : "";
  if (roleId !== baseline.roleId && role?.id) {
    data.company_role_id = Number(role.id);
  }

  const { dial_code, phone_number } = splitPhoneForApi(
    phone,
    phoneCountryDialCode,
  );
  if (
    dial_code !== baseline.dial_code ||
    phone_number !== baseline.phone_number
  ) {
    data.phone_number = phone_number || null;
    data.dial_code = phone_number ? dial_code : null;
  }

  if (pictureFile && profilePictureUrl) {
    data.profile_picture_url = profilePictureUrl;
  } else if (!picturePreview && employeeImage) {
    data.profile_picture_url = "";
  }

  return data;
};

/** Build the list query params. FE default page size is 50 (per design). The
 * backend filters by `name` (case-insensitive partial match) and paginates via
 * `page` / `items`, returning counts in response headers. */
export const buildCompanyUsersListParams = ({ search, page, pageSize }) => {
  const params = { page, items: pageSize };
  const term = (search || "").trim();
  if (term) {
    params.name = term;
  }
  return params;
};
